import React from "react"
import styled from "styled-components"
import { useIntl } from "gatsby-plugin-intl"
import { FiArrowDown, FiArrowRight, FiArrowUpRight } from "react-icons/fi"
import Link from "../components/Link"
import PageMetadata from "../components/PageMetadata"
import { translateMessageId } from "../utils/translations"
import projectLogo from "../assets/partners/new-project-ai.svg"
import iaLogo from "../assets/partners/newia-tech.png"
import netwLogo from "../assets/partners/netw-consulting.webp"

const Page = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.text};
  h1,
  h2,
  h3 {
    font-family: inherit;
    font-style: normal;
  }
  a:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 5px;
  }
`
const Container = styled.div`
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 0 2.5rem;
  @media (max-width: 600px) {
    padding: 0 1.25rem;
  }
`
const Hero = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  color: #fff;
  background: #0b1b2a;
  padding: 4.5rem 0;
  &::before,
  &::after {
    content: "";
    position: absolute;
    z-index: -1;
    pointer-events: none;
    width: 550px;
    height: 550px;
    border: 1px solid rgba(138, 174, 224, 0.16);
    border-radius: 50%;
    top: -240px;
    right: -80px;
  }
  &::after {
    top: -175px;
    right: -15px;
  }
  a:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 700px) {
    padding: 3rem 0;
  }
`
const Eyebrow = styled.p`
  margin: 0 0 1.25rem;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
`
const HeroKicker = styled(Eyebrow)`
  color: #c1d1e4;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  &::before {
    content: "";
    width: 2rem;
    height: 2px;
    background: #6d9dff;
  }
`
const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 4rem;
  align-items: end;
  h1 {
    color: #fff;
    font-size: clamp(2.7rem, 4.8vw, 4.5rem);
    font-weight: 500;
    letter-spacing: -0.045em;
    line-height: 1.08;
    margin: 0;
    span {
      display: block;
    }
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`
const HeroIntro = styled.div`
  max-width: 440px;
  p {
    margin: 0 0 1.5rem;
    color: #d1dae5;
    font-size: 1.1rem;
    line-height: 1.7;
  }
  a {
    display: inline-flex;
    align-items: center;
    gap: 1.25rem;
    color: #fff;
    text-decoration: none;
    padding: 0.5rem 0;
    border-bottom: 1px solid #718197;
    font-size: 0.95rem;
  }
  a:hover {
    border-color: #fff;
  }
`
const Network = styled.section`
  padding: 4.5rem 0 5rem;
  scroll-margin-top: 6rem;
  h2 {
    margin: 0;
    font-size: clamp(1.8rem, 3vw, 2.5rem);
    line-height: 1.18;
    letter-spacing: -0.035em;
    font-weight: 500;
  }
  @media (max-width: 600px) {
    padding: 3rem 0;
  }
`
const SectionHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 2.5rem;
  > p {
    max-width: 290px;
    margin: 0;
    color: ${({ theme }) => theme.colors.text200};
    line-height: 1.65;
    font-size: 0.95rem;
  }
  @media (max-width: 800px) {
    flex-direction: column;
    align-items: flex-start;
    > p {
      max-width: 100%;
    }
  }
`
const PartnerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
  @media (max-width: 950px) {
    grid-template-columns: 1fr;
  }
`
const PartnerCard = styled.article`
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  background: ${({ theme }) => theme.colors.background};
  transition: border-color 180ms ease, box-shadow 180ms ease;
  &:hover,
  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 12px 32px rgba(10, 26, 45, 0.07);
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
const Brand = styled.div`
  height: 165px;
  padding: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  background: ${({ $variant }) =>
    $variant === "ia"
      ? "#111820"
      : $variant === "project"
      ? "#f2f4fa"
      : "#fff"};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  color: #15263d;
  img {
    display: block;
    max-width: 100%;
    object-fit: contain;
    margin: 0;
    transform: ${({ $variant }) =>
      $variant === "netw" ? "translateY(1rem)" : "none"};
  }
  strong {
    font-size: 1.4rem;
    line-height: 1.2;
    letter-spacing: -0.04em;
    font-weight: 600;
  }
`
const CardBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 1.75rem;
  ${Eyebrow} {
    font-size: 0.65rem;
    letter-spacing: 0.1em;
    margin-bottom: 0.8rem;
  }
  h3 {
    margin: 0 0 1rem;
    font-size: 1.5rem;
    line-height: 1.2;
    font-weight: 600;
    letter-spacing: -0.035em;
  }
  > p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.95rem;
    line-height: 1.75;
    margin: 0 0 1.5rem;
  }
  @media (max-width: 380px) {
    padding: 1.25rem;
  }
`
const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: auto;
  margin-bottom: 1.75rem;
  span {
    padding: 0.4rem 0.6rem;
    font-size: 0.7rem;
    line-height: 1.3;
    color: ${({ theme }) => theme.colors.text200};
    background: ${({ theme }) => theme.colors.ednBackground};
  }
`
const CardLinks = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  min-height: 5rem;
  padding-top: 1.25rem;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 0.85rem;
  a {
    display: inline-flex;
    align-items: center;
    gap: 0.7rem;
    font-size: 0.9rem;
    line-height: 1.4;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
  svg {
    flex-shrink: 0;
  }
`
const Contact = styled.section`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
  padding: 3.5rem 0;
`
const ContactInner = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  h2 {
    max-width: 600px;
    font-size: clamp(1.8rem, 3vw, 2.4rem);
    line-height: 1.2;
    letter-spacing: -0.035em;
    margin: 0 0 1rem;
    font-weight: 500;
  }
  p:last-child {
    max-width: 610px;
    color: ${({ theme }) => theme.colors.text200};
    line-height: 1.7;
    margin: 0;
  }
  @media (max-width: 850px) {
    flex-direction: column;
    align-items: flex-start;
  }
`
const ContactLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  flex-shrink: 0;
  padding: 1rem 1.25rem;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background} !important;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  svg {
    flex-shrink: 0;
  }
`

const partners = [
  {
    id: "project",
    name: "New Project AI",
    logo: projectLogo,
    url: "https://www.newprojectai.com/",
    profile: "/info/new-project-ai/",
  },
  { id: "ia", name: "NewIA Tech", logo: iaLogo, url: "https://newiatech.com/" },
  {
    id: "netw",
    name: "NetW Consulting",
    logo: netwLogo,
    url: "https://netwconsulting.com/",
  },
]

const PartnersPage = () => {
  const intl = useIntl()
  const t = (id) => translateMessageId(id, intl)
  return (
    <Page>
      <PageMetadata
        title={t("partners-meta-title")}
        description={t("partners-meta-description")}
      />
      <Hero>
        <Container>
          <HeroKicker>{t("partners-eyebrow")}</HeroKicker>
          <HeroGrid>
            <h1>
              {t("partners-title-first")}{" "}
              <span>{t("partners-title-second")}</span>
            </h1>
            <HeroIntro>
              <p>{t("partners-intro")}</p>
              <Link to="#red-de-aliados">
                {t("partners-discover")} <FiArrowDown aria-hidden="true" />
              </Link>
            </HeroIntro>
          </HeroGrid>
        </Container>
      </Hero>
      <Network id="red-de-aliados" aria-labelledby="partners-network-title">
        <Container>
          <SectionHeader>
            <div>
              <Eyebrow>{t("partners-network-eyebrow")}</Eyebrow>
              <h2 id="partners-network-title">{t("partners-network-title")}</h2>
            </div>
            <p>{t("partners-network-description")}</p>
          </SectionHeader>
          <PartnerGrid>
            {partners.map((partner) => (
              <PartnerCard
                key={partner.id}
                aria-labelledby={`partner-${partner.id}`}
              >
                <Brand $variant={partner.id} aria-hidden="true">
                  <img
                    src={partner.logo}
                    alt=""
                    width={partner.id === "project" ? 40 : 230}
                    height={
                      partner.id === "project"
                        ? 40
                        : partner.id === "ia"
                        ? 68
                        : 123
                    }
                  />
                  {partner.id === "project" && <strong>New Project AI</strong>}
                </Brand>
                <CardBody>
                  <Eyebrow>{t(`partners-${partner.id}-category`)}</Eyebrow>
                  <h3 id={`partner-${partner.id}`}>{partner.name}</h3>
                  <p>{t(`partners-${partner.id}-description`)}</p>
                  <Tags>
                    <span>{t(`partners-${partner.id}-tag-one`)}</span>
                    <span>{t(`partners-${partner.id}-tag-two`)}</span>
                  </Tags>
                  <CardLinks>
                    <Link
                      to={partner.url}
                      hideArrow
                      ariaLabel={`${t("partners-website")}: ${
                        partner.name
                      } (${t("partners-new-tab")})`}
                    >
                      {t("partners-website")}{" "}
                      <FiArrowUpRight aria-hidden="true" />
                    </Link>
                    {partner.profile && (
                      <Link to={partner.profile}>
                        {t("partners-profile")}{" "}
                        <FiArrowRight aria-hidden="true" />
                      </Link>
                    )}
                  </CardLinks>
                </CardBody>
              </PartnerCard>
            ))}
          </PartnerGrid>
        </Container>
      </Network>
      <Contact>
        <ContactInner>
          <div>
            <Eyebrow>{t("partners-contact-eyebrow")}</Eyebrow>
            <h2>{t("partners-contact-title")}</h2>
            <p>{t("partners-contact-description")}</p>
          </div>
          <ContactLink to="/#contacto">
            {t("partners-contact-cta")} <FiArrowRight aria-hidden="true" />
          </ContactLink>
        </ContactInner>
      </Contact>
    </Page>
  )
}

export default PartnersPage
